import React, { useMemo } from 'react';
import GenButton from './GenButton';
import './GenButton.css';
import Tooltip from '../utils/Tooltip';
import './GenerationRunControl.css';
import useAsymptoticGauge, { gaugePhaseFromStatus } from '../../hooks/useAsymptoticGauge';

const TXT_INITIALIZING = { fr: 'Initialisation...', en: 'Initializing...', de: 'Initialisierung...', es: 'Inicializando...', pt: 'Inicializando...' };
const TXT_QUEUED = { fr: 'En file d\'attente...', en: 'Queued...', de: 'In der Warteschlange...', es: 'En cola...', pt: 'Na fila...' };
const TXT_BOOTING = { fr: 'D\u00e9marrage du solveur...', en: 'Starting solver...', de: 'Solver startet...', es: 'Iniciando el solver...', pt: 'Iniciando o solver...' };
const TXT_GENERATING = { fr: 'G\u00e9n\u00e9ration...', en: 'Generating...', de: 'Generierung...', es: 'Generando...', pt: 'Gerando...' };
const TXT_FINALIZING = { fr: 'Finalisation...', en: 'Finalizing...', de: 'Abschluss...', es: 'Finalizando...', pt: 'Finalizando...' };

const BUSY_STATUSES = new Set(['queued', 'booting', 'initializing', 'loading']);

export function isGenerationRunBusy(status) {
    return BUSY_STATUSES.has(status);
}

function GenerationBusyStatus({
    status,
    lang = 'fr',
    timeLimit,
    blocked = false,
    blockedLabel = null,
    layout = 'default',
    localSolve = false,
}) {
    const isBusyPhase = BUSY_STATUSES.has(status) && !blocked;
    const gaugeRef = useAsymptoticGauge(
        isBusyPhase,
        timeLimit,
        gaugePhaseFromStatus(status),
        localSolve,
    );
    const isSheet = layout === 'sheet';

    const statusLabel = useMemo(() => {
        if (blocked) return blockedLabel;
        if (status === 'initializing') return TXT_INITIALIZING[lang] || TXT_INITIALIZING.en;
        if (status === 'queued') return TXT_QUEUED[lang] || TXT_QUEUED.en;
        if (status === 'booting') return TXT_BOOTING[lang] || TXT_BOOTING.en;
        if (status === 'finalizing') return TXT_FINALIZING[lang] || TXT_FINALIZING.en;
        return TXT_GENERATING[lang] || TXT_GENERATING.en;
    }, [blocked, blockedLabel, lang, status]);

    const gauge = isBusyPhase ? (
        <div
            className={[
                'generation-run-control__busy-gauge',
                isSheet ? 'generation-run-control__busy-gauge--sheet' : '',
            ].filter(Boolean).join(' ')}
            ref={gaugeRef}
        />
    ) : null;

    const spinner = !blocked ? (
        <span className="generation-run-control__busy-spinner" aria-hidden="true" />
    ) : null;
    const text = <span className="generation-run-control__busy-label">{statusLabel}</span>;

    // Default: gauge + label share one pill. Sheet: split so CSS can put the
    // gauge behind the whole handle and keep the label centered.
    if (isSheet) {
        return (
            <>
                {gauge}
                <div
                    className={[
                        'generation-run-control__busy-status',
                        'generation-run-control__busy-status--sheet',
                        blocked ? 'generation-run-control__busy-status--blocked' : '',
                    ].filter(Boolean).join(' ')}
                >
                    {spinner}
                    {text}
                </div>
            </>
        );
    }

    return (
        <div className={`generation-run-control__busy-status${blocked ? ' generation-run-control__busy-status--blocked' : ''}`}>
            {gauge}
            {spinner}
            {text}
        </div>
    );
}

export default function GenerationRunControl({
    status,
    lang = 'fr',
    timeLimit,
    onGenerate,
    onStop,
    disabled = false,
    hasTargets = true,
    invalidBuildReason = null,
    tooltipContent = null,
    tooltipWrapperClassName = 'generate-gen-button-tooltip-wrap',
    className = '',
    statusClassName = '',
    buttonClassName = '',
    showStatus = true,
    showButton = true,
    showBlockedStatus = false,
    statusLayout = 'default',
    /** This run is solving in the browser, so the gauge spans the whole budget. */
    localSolve = false,
    iconOnly = true,
    noIcon = false,
    noIdleIcon = false,
}) {
    const isBusy = isGenerationRunBusy(status);
    const hasBlockingInvalidBuild = !isBusy
        && typeof invalidBuildReason === 'string'
        && invalidBuildReason.trim().length > 0;
    const shouldShowStatus = showStatus && (isBusy || (showBlockedStatus && hasBlockingInvalidBuild));
    const shouldShowButton = showButton && (!hasBlockingInvalidBuild || isBusy);
    const isSheetStatus = statusLayout === 'sheet';
    const rootClassName = [
        'generation-run-control',
        isBusy ? 'generation-run-control--busy' : '',
        hasBlockingInvalidBuild ? 'generation-run-control--blocked' : '',
        isSheetStatus ? 'generation-run-control--sheet' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <div className={rootClassName}>
            {shouldShowStatus ? (
                <div className={`generation-run-control__status-fill ${statusClassName}`.trim()}>
                    <GenerationBusyStatus
                        status={status}
                        lang={lang}
                        timeLimit={timeLimit}
                        blocked={hasBlockingInvalidBuild}
                        blockedLabel={invalidBuildReason}
                        layout={statusLayout}
                        localSolve={localSolve}
                    />
                </div>
            ) : null}
            {shouldShowButton ? (
                <div className={`generation-run-control__button ${buttonClassName}`.trim()}>
                    <Tooltip
                        content={isBusy ? null : tooltipContent}
                        wrapperClassName={tooltipWrapperClassName}
                    >
                        <GenButton
                            onClick={onGenerate}
                            onStop={onStop}
                            status={status}
                            disabled={disabled}
                            hasTargets={hasTargets}
                            invalidBuildReason={invalidBuildReason}
                            lang={lang}
                            timeLimit={timeLimit}
                            localSolve={localSolve}
                            forceStopVisual={isBusy}
                            noIcon={noIcon}
                            noIdleIcon={noIdleIcon}
                            iconOnly={iconOnly}
                        />
                    </Tooltip>
                </div>
            ) : null}
        </div>
    );
}
